'use client'

import {
  createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import Artwork from '@/components/Artwork'
import {
  Play, ListEnd, ListStart, Heart, ListMusic, ListPlus, Mic2, Download, Maximize2, Check, X, Radio,
} from 'lucide-react'
import { usePlayer, type PlayerTrack } from '@/lib/player-context'
import { useFavorites, usePlaylists } from '@/lib/library'
import { useParty } from '@/lib/party-context'
import { saveDownload, getDownload } from '@/lib/downloads'
import { uploadLocalForBaffle } from '@/lib/baffle-upload'
import { showToast, updateToast, dismissToast } from '@/lib/toast'

/**
 * Menu contextuel d'un morceau, ouvert au CLIC DROIT (ordi) ou à l'APPUI LONG
 * (mobile / tablette). Actions : Lire, File d'attente, Favoris, Ajouter à une
 * playlist (+ créer), Chercher les paroles (en arrière-plan), Télécharger, Ouvrir.
 *
 * Usage :
 *   1. Englober l'app dans <TrackMenuProvider> (fait dans Providers).
 *   2. Sur n'importe quel élément de piste : const bind = useTrackMenu();
 *      <button {...bind(track)} …>
 */

type Pos = { x: number; y: number }
type MenuCtx = { open: (track: PlayerTrack, pos: Pos) => void }
const Ctx = createContext<MenuCtx>({ open: () => {} })

/** Renvoie une fonction `bind(track)` à étaler sur un élément cliquable. */
export function useTrackMenu() {
  const { open } = useContext(Ctx)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const moved = useRef(false)
  const suppress = useRef(false)   // bloque le clic qui suit un appui long

  return useCallback((track: PlayerTrack) => ({
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      open(track, { x: e.clientX, y: e.clientY })
    },
    onTouchStart: (e: React.TouchEvent) => {
      moved.current = false
      const t = e.touches[0]
      const x = t.clientX, y = t.clientY
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        if (moved.current) return
        suppress.current = true
        try { navigator.vibrate?.(18) } catch {}
        open(track, { x, y })
      }, 480)
    },
    onTouchMove: () => { moved.current = true; if (timer.current) clearTimeout(timer.current) },
    onTouchEnd: () => { if (timer.current) clearTimeout(timer.current) },
    // L'appui long déclenche le menu ; on annule le "clic" qui suivrait (sinon
    // ça lancerait aussi la lecture). Capture phase = avant le onClick de l'élément.
    onClickCapture: (e: React.MouseEvent) => {
      if (suppress.current) { e.preventDefault(); e.stopPropagation(); suppress.current = false }
    },
  }), [open])
}

export function TrackMenuProvider({ children }: { children: ReactNode }) {
  const [menu, setMenu] = useState<{ track: PlayerTrack; pos: Pos } | null>(null)
  const open = useCallback((track: PlayerTrack, pos: Pos) => setMenu({ track, pos }), [])
  return (
    <Ctx.Provider value={{ open }}>
      {children}
      {menu && <TrackMenu track={menu.track} pos={menu.pos} onClose={() => setMenu(null)} />}
    </Ctx.Provider>
  )
}

const MENU_W = 248

function TrackMenu({ track, pos, onClose }: { track: PlayerTrack; pos: Pos; onClose: () => void }) {
  const router = useRouter()
  const { play, enqueue, playNext, current } = usePlayer()
  const { has: hasFav, toggle: toggleFav } = useFavorites()
  const { playlists, create, addTrack } = usePlaylists()
  const { role, addToBaffle } = useParty()
  const ref = useRef<HTMLDivElement | null>(null)
  const [creating, setCreating] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const isFav = hasFav(track.videoId)
  // Envoi baffle dispo dès qu'une TV est connue : soirée EN COURS **ou** simple
  // TÉLÉCOMMANDE QR (code mémorisé). Avant, l'option n'apparaissait qu'en
  // soirée (role !== 'off') → invisible en télécommande pure, alors que la page
  // bibliothèque, elle, l'affichait : incohérence corrigée ici.
  const [remoteCode, setRemoteCode] = useState('')
  useEffect(() => { try { setRemoteCode(localStorage.getItem('blc.remote.lastcode') || '') } catch {} }, [])
  const canBaffle = role !== 'off' || !!remoteCode

  // Position dans le viewport
  const margin = 8
  let x = pos.x
  if (x + MENU_W > window.innerWidth - margin) x = window.innerWidth - MENU_W - margin
  if (x < margin) x = margin
  const estH = 380
  let y = pos.y
  if (y + estH > window.innerHeight - margin) y = Math.max(margin, window.innerHeight - estH - margin)

  // Fermeture (clic dehors / Escape / scroll)
  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const act = (fn: () => void) => { fn(); onClose() }

  const onLire = () => act(() => play(track, [track]))
  const onPlayNext = () => act(() => {
    playNext(track)
    showToast({ kind: 'success', msg: 'Sera lu juste après', thumbnail: track.thumbnail, duration: 2000 })
  })
  const onQueue = () => act(() => {
    enqueue(track)
    showToast({ kind: 'success', msg: "Ajouté à la file d'attente", thumbnail: track.thumbnail, duration: 2000 })
  })
  const onFav = () => act(() => {
    toggleFav(track)
    showToast({ kind: 'success', msg: isFav ? 'Retiré des favoris' : 'Ajouté aux favoris', thumbnail: track.thumbnail, duration: 2000 })
  })
  const onAddPlaylist = (id: string, title: string) => act(() => {
    addTrack(id, track)
    showToast({ kind: 'success', msg: `Ajouté à « ${title} »`, thumbnail: track.thumbnail, duration: 2200 })
  })
  const onCreate = (e?: React.FormEvent) => {
    e?.preventDefault()
    const title = newTitle.trim() || 'Nouvelle playlist'
    const id = create(title)
    addTrack(id, track)
    showToast({ kind: 'success', msg: `Playlist « ${title} » créée`, thumbnail: track.thumbnail, duration: 2500 })
    onClose()
  }
  // Recherche des paroles EN ARRIÈRE-PLAN (ne bloque rien, marche même si une
  // autre musique joue). Résultat mis en cache pour le karaoké.
  const onLyrics = () => act(() => {
    showToast({ kind: 'info', msg: 'Recherche des paroles en arrière-plan…', thumbnail: track.thumbnail, duration: 2500 })
    fetch('/api/lyrics/auto-sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ videoId: track.videoId, title: track.title, artist: track.artist }),
    })
      .then(r => r.json().catch(() => null))
      .then(j => {
        if (j && (j.ok || (Array.isArray(j.lines) && j.lines.length))) {
          showToast({ kind: 'success', msg: `Paroles trouvées : ${track.title}`, thumbnail: track.thumbnail, duration: 3000 })
        } else {
          showToast({ kind: 'info', msg: `Pas de paroles trouvées pour ${track.title}`, duration: 3000 })
        }
      })
      .catch(() => {})
  })
  const onDownload = () => act(async () => {
    const existing = await getDownload(track.videoId)
    if (existing) { showToast({ kind: 'info', msg: 'Déjà dans ta bibliothèque', duration: 2200 }); return }
    showToast({ kind: 'info', msg: `Téléchargement de « ${track.title} »…`, thumbnail: track.thumbnail, duration: 2500 })
    try {
      const r = await fetch(`/api/youtube/download?v=${encodeURIComponent(track.videoId)}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const blob = await r.blob()
      await saveDownload({
        videoId: track.videoId, title: track.title, artist: track.artist, thumbnail: track.thumbnail,
        fileName: `${(track.title || track.videoId).replace(/[<>:"/\\|?*\x00-\x1F]/g, '')}.mp3`,
        size: blob.size, savedAt: Date.now(), blob,
      })
      showToast({ kind: 'success', msg: `Téléchargé : ${track.title}`, thumbnail: track.thumbnail, duration: 3000 })
    } catch {
      showToast({ kind: 'error', msg: 'Téléchargement impossible (réessaie)', duration: 3500 })
    }
  })
  const onOuvrir = () => act(() => { play(track, [track]); router.push('/app/player') })
  // Envoi vers la TV : on ferme le menu tout de suite, puis on affiche une
  // BARRE DE PROGRESSION (l'upload d'un fichier local peut être long) et on
  // enchaîne l'ajout à la file de la TV. Le retry est géré dans
  // uploadLocalForBaffle (encaisse les coupures réseau du mobile).
  const onBaffle = () => {
    onClose()
    ;(async () => {
      const toastId = showToast({ kind: 'progress', msg: `Envoi de « ${track.title} » sur la télé…`, progress: 0.03, thumbnail: track.thumbnail, duration: -1 })
      let hadLocal = false
      let audioUrl: string | undefined = (track as any).audioUrl
      try {
        const dl = await getDownload(track.videoId)
        if (dl?.blob) {
          hadLocal = true
          audioUrl = (await uploadLocalForBaffle(dl.blob, dl.fileName, dl.videoId, (f) =>
            updateToast(toastId, { progress: Math.max(0.03, f * 0.97), msg: `Envoi vers la télé… ${Math.round(f * 100)} %` }))) || audioUrl
        }
      } catch {}
      const ok = await addToBaffle({ ...track, audioUrl } as any)
      dismissToast(toastId)
      showToast({
        kind: ok && !(hadLocal && !audioUrl) ? 'success' : 'error',
        msg: ok
          ? (hadLocal && !audioUrl
              ? "Envoi coupé — réessaie en gardant l'appli ouverte"
              : `Envoyé sur la télé : ${track.title}`)
          : 'Télé introuvable — reconnecte-toi au QR',
        thumbnail: track.thumbnail, duration: 3200,
      })
    })()
  }

  if (typeof document === 'undefined') return null

  const Item = ({ icon, label, onClick, danger = false }: { icon: ReactNode; label: string; onClick: () => void; danger?: boolean }) => (
    <button onClick={onClick}
      className={`group/it w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-a2/[0.12] transition ${danger ? 'text-rose-300' : ''}`}>
      <span className="shrink-0 w-7 h-7 rounded-lg grid place-items-center bg-white/[0.05] text-white/75 group-hover/it:text-a5 group-hover/it:bg-a2/20 transition">{icon}</span>
      <span className="text-sm flex-1">{label}</span>
    </button>
  )

  return createPortal(
    <div ref={ref}
      style={{ position: 'fixed', left: x, top: y, width: MENU_W, zIndex: 1200, background: 'linear-gradient(180deg, #16183C 0%, rgb(var(--spanel)) 100%)' }}
      className="rounded-2xl border border-white/[0.08] overflow-hidden animate-[blcMenuIn_140ms_ease] shadow-[0_20px_50px_rgba(0,0,0,.6),0_0_30px_rgb(var(--a2)/.18)]">
      <style>{`@keyframes blcMenuIn{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}`}</style>

      {/* Entête piste */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-white/[0.07] bg-white/[0.03]">
        {track.isVideo
          ? (track.thumbnail ? <img src={track.thumbnail} alt="" className="w-10 h-10 rounded-xl object-cover" referrerPolicy="no-referrer" /> : <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-adeep to-spanel" />)
          : <Artwork track={track} size={40} strict rounded="rounded-xl" className="shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold truncate">{track.title}</div>
          <div className="text-[11px] text-white/50 truncate">{track.artist}</div>
        </div>
        <button onClick={onClose} aria-label="Fermer" className="w-7 h-7 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/[0.08]"><X size={14} /></button>
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-1.5">
        {canBaffle && (
          <button onClick={onBaffle}
            className="w-full mb-1 flex items-center gap-3 px-3 py-2 text-left rounded-xl bg-gradient-to-r from-[#2563EB]/30 to-[#22D3EE]/20 hover:from-[#2563EB]/45 hover:to-[#22D3EE]/30 border border-[#3B82F6]/30 transition">
            <span className="w-7 h-7 rounded-lg grid place-items-center bg-gradient-to-br from-[#22D3EE] to-[#2563EB] text-white"><Radio size={15} /></span>
            <span className="text-sm flex-1 font-semibold">Envoyer sur la télé 🔊</span>
          </button>
        )}
        <Item icon={<Play size={16} />} label="Lire" onClick={onLire} />
        {current && current.videoId !== track.videoId && (
          <Item icon={<ListStart size={16} />} label="Lire ensuite" onClick={onPlayNext} />
        )}
        <Item icon={<ListEnd size={16} />} label="Ajouter à la file d'attente" onClick={onQueue} />
        <Item icon={<Heart size={16} className={isFav ? 'fill-a3 text-a3' : ''} />} label={isFav ? 'Retirer des favoris' : 'Favoris'} onClick={onFav} />

        {/* Playlists */}
        <div className="border-t border-white/[0.07] mt-1 pt-1">
          {playlists.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-white/40">Ajouter à une playlist</div>
              <div className="max-h-40 overflow-y-auto">
                {playlists.map(p => {
                  const already = p.tracks.some(t => t.videoId === track.videoId)
                  return (
                    <button key={p.id} onClick={() => onAddPlaylist(p.id, p.title)} disabled={already}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl hover:bg-a2/[0.12] transition disabled:opacity-50">
                      <ListMusic size={14} className="text-a4 shrink-0" />
                      <span className="text-sm flex-1 truncate">{p.title}</span>
                      {already ? <Check size={13} className="text-emerald-400" /> : <span className="text-[10px] text-white/40">{p.tracks.length}</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}
          {creating ? (
            <form onSubmit={onCreate} className="p-2 flex items-center gap-2">
              <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
                placeholder="Nom de la playlist…"
                className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded-xl px-2.5 py-1.5 text-xs focus:outline-none focus:border-a2/70" />
              <button type="submit" className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-white bg-gradient-to-r from-a1 to-a2 shadow-[0_0_14px_rgb(var(--a2)/.45)]">Créer</button>
            </form>
          ) : (
            <button onClick={() => setCreating(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl text-a4 hover:bg-a2/[0.12] transition">
              <ListPlus size={16} />
              <span className="text-sm flex-1 font-semibold">Créer une playlist…</span>
            </button>
          )}
        </div>

        <div className="border-t border-white/[0.07] mt-1 pt-1">
          <Item icon={<Mic2 size={16} />} label="Chercher les paroles" onClick={onLyrics} />
          <Item icon={<Download size={16} />} label="Télécharger" onClick={onDownload} />
          <Item icon={<Maximize2 size={16} />} label="Ouvrir dans le lecteur" onClick={onOuvrir} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
